import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Lock,
  Unlock,
  Search,
  ShieldAlert,
  Users,
  Clock,
  FilterX,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/hooks/use-auth";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { TableSkeleton } from "@/components/loading";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  fetchLockedUsers,
  unlockUser,
  bulkUnlockUsers,
  type LockedUserRow,
  type LockedUsersFilterQuery,
} from "@/lib/api/locked-users";

export const Route = createFileRoute("/_authenticated/admin/locked-users")({
  component: LockedUsersPage,
});

const PAGE_SIZE = 25;

function LockedUsersPage() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  // ── State ──
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Unlock dialog
  const [unlockTarget, setUnlockTarget] = useState<LockedUserRow | null>(null);
  const [bulkUnlockOpen, setBulkUnlockOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");

  if (!isAdmin) {
    return <AccessDenied message="You don't have permission to manage locked users." />;
  }

  // ── Query ──
  const queryParams: LockedUsersFilterQuery = useMemo(
    () => ({
      pageNumber: page,
      pageSize: PAGE_SIZE,
      search: search || undefined,
      role: roleFilter !== "all" ? roleFilter : undefined,
      sortDesc: true,
    }),
    [page, search, roleFilter],
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "locked-users", queryParams],
    queryFn: () => fetchLockedUsers(queryParams),
  });

  const users = data?.items ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalCount = data?.totalCount ?? 0;

  // ── Mutations ──
  const unlockMutation = useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
      unlockUser(userId, reason),
    onSuccess: () => {
      toast.success("User unlocked successfully.");
      queryClient.invalidateQueries({ queryKey: ["admin", "locked-users"] });
      setUnlockTarget(null);
      setUnlockReason("");
    },
    onError: (err: any) => {
      toast.error(err?.message ?? "Failed to unlock user.");
    },
  });

  const bulkUnlockMutation = useMutation({
    mutationFn: ({ userIds, reason }: { userIds: string[]; reason?: string }) =>
      bulkUnlockUsers(userIds, reason),
    onSuccess: (result) => {
      if (result.failureCount > 0) {
        toast.warning(
          `Unlocked ${result.successCount} user(s), ${result.failureCount} failed.`,
        );
      } else {
        toast.success(`Successfully unlocked ${result.successCount} user(s).`);
      }
      queryClient.invalidateQueries({ queryKey: ["admin", "locked-users"] });
      setBulkUnlockOpen(false);
      setSelected(new Set());
      setUnlockReason("");
    },
    onError: (err: any) => {
      toast.error(err?.message ?? "Bulk unlock failed.");
    },
  });

  // ── Selection helpers ──
  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === users.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(users.map((u) => u.id)));
    }
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  function clearFilters() {
    setSearch("");
    setRoleFilter("all");
    setPage(1);
  }

  const hasActiveFilters = !!search || roleFilter !== "all";

  // ── Format helpers ──
  function formatDate(dateStr: string | null) {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString();
  }

  function timeUntil(dateStr: string | null) {
    if (!dateStr) return "—";
    const end = new Date(dateStr);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    if (diff <= 0) return "Expired";
    const mins = Math.ceil(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}h ${remainingMins}m`;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Lock className="h-6 w-6 text-destructive" />
            Locked Users
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage accounts locked due to failed login attempts.
            {totalCount > 0 && (
              <span className="ml-2 font-medium text-destructive">
                {totalCount} locked account{totalCount !== 1 ? "s" : ""}
              </span>
            )}
          </p>
        </div>
        {selected.size > 0 && (
          <Button
            variant="default"
            onClick={() => setBulkUnlockOpen(true)}
            className="gap-2"
          >
            <Unlock className="h-4 w-4" />
            Unlock {selected.size} User{selected.size !== 1 ? "s" : ""}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or username…"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="SuperAdmin">Super Admin</SelectItem>
            <SelectItem value="SystemAdmin">System Admin</SelectItem>
            <SelectItem value="TeamLeader">Team Leader</SelectItem>
            <SelectItem value="ContentCreator">Content Creator</SelectItem>
            <SelectItem value="Student">Student</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
            <FilterX className="h-4 w-4" />
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton rows={5} columns={7} />
      ) : isError ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <AlertTriangle className="h-12 w-12 text-destructive mb-3" />
          <p className="text-lg font-semibold text-foreground">Failed to load locked users</p>
          <p className="text-sm text-muted-foreground">Please try again later.</p>
        </div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ShieldAlert className="h-16 w-16 text-muted-foreground/40 mb-4" />
          <p className="text-lg font-semibold text-foreground">No locked accounts</p>
          <p className="text-sm text-muted-foreground max-w-md">
            {hasActiveFilters
              ? "No locked users match your current filters."
              : "Great news! There are currently no users locked out of their accounts."}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={selected.size === users.length && users.length > 0}
                      onCheckedChange={toggleAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                  <TableHead>Full Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-center">Failed Attempts</TableHead>
                  <TableHead>Lockout Ends</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(user.id)}
                        onCheckedChange={() => toggleSelect(user.id)}
                        aria-label={`Select ${user.fullName || user.email}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {user.fullName || "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.email}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.userName || "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.roles.map((role) => (
                          <Badge key={role} variant="outline" className="text-xs">
                            {role}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-mono">
                      <Badge variant="destructive" className="text-xs">
                        {user.accessFailedCount}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm">
                        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                        <span title={formatDate(user.lockoutEnd)}>
                          {timeUntil(user.lockoutEnd)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.isCurrentlyLocked ? (
                        <Badge variant="destructive" className="gap-1">
                          <Lock className="h-3 w-3" />
                          Locked
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Expired</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => setUnlockTarget(user)}
                        disabled={unlockMutation.isPending}
                      >
                        <Unlock className="h-3.5 w-3.5" />
                        Unlock
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={page <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        onClick={() => setPage(pageNum)}
                        isActive={page === pageNum}
                        className="cursor-pointer"
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className={page >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}

      {/* Single Unlock Dialog */}
      <Dialog open={!!unlockTarget} onOpenChange={(open) => { if (!open) { setUnlockTarget(null); setUnlockReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unlock User Account</DialogTitle>
            <DialogDescription>
              This will reset the failed login attempts and remove the lockout for{" "}
              <span className="font-semibold text-foreground">
                {unlockTarget?.fullName || unlockTarget?.email}
              </span>
              . The user will be able to log in immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="unlock-reason">Reason (optional)</Label>
            <Textarea
              id="unlock-reason"
              placeholder="e.g. User contacted support, identity verified…"
              value={unlockReason}
              onChange={(e) => setUnlockReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUnlockTarget(null); setUnlockReason(""); }}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (unlockTarget)
                  unlockMutation.mutate({
                    userId: unlockTarget.id,
                    reason: unlockReason || undefined,
                  });
              }}
              disabled={unlockMutation.isPending}
            >
              {unlockMutation.isPending ? "Unlocking…" : "Confirm Unlock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Unlock Dialog */}
      <Dialog open={bulkUnlockOpen} onOpenChange={(open) => { if (!open) { setBulkUnlockOpen(false); setUnlockReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Unlock Users</DialogTitle>
            <DialogDescription>
              You are about to unlock{" "}
              <span className="font-semibold text-foreground">{selected.size}</span> user
              account{selected.size !== 1 ? "s" : ""}. All failed login attempts will be
              reset and lockouts removed immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="bulk-unlock-reason">Reason (optional)</Label>
            <Textarea
              id="bulk-unlock-reason"
              placeholder="e.g. Mass lockout after security incident resolved…"
              value={unlockReason}
              onChange={(e) => setUnlockReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkUnlockOpen(false); setUnlockReason(""); }}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                bulkUnlockMutation.mutate({
                  userIds: Array.from(selected),
                  reason: unlockReason || undefined,
                })
              }
              disabled={bulkUnlockMutation.isPending}
            >
              {bulkUnlockMutation.isPending
                ? "Unlocking…"
                : `Unlock ${selected.size} User${selected.size !== 1 ? "s" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
